// src/lib/crudFactory.ts
import { atom } from 'jotai';
import {
  updateDoc,
  deleteDoc,
  collection,
  doc,
  serverTimestamp,
  FirestoreDataConverter,
  WithFieldValue,
  DocumentData,
  FirestoreError,
  PartialWithFieldValue,
  SetOptions,
  QueryDocumentSnapshot,
  SnapshotOptions,
  setDoc,
} from 'firebase/firestore';
import { auth, db, refreshFirebaseToken } from 'utils/firebase';

function makeConverter<T>(): FirestoreDataConverter<T> {
  // We write a single implementation, but then assert that it's a FirestoreDataConverter
  const impl = {
    // single function covers both overloads:
    toFirestore(
      modelObject: WithFieldValue<T> | PartialWithFieldValue<T>,
      options?: SetOptions
    ): DocumentData | PartialWithFieldValue<DocumentData> {
      // Strip undefined values and serialize Dates
      const cleanObject = Object.fromEntries(
        Object.entries(modelObject as any).filter(([_, value]) => value !== undefined)
      );
      return cleanObject;
    },
    fromFirestore(snapshot: QueryDocumentSnapshot<DocumentData>, options: SnapshotOptions): T {
      return snapshot.data(options) as T;
    },
  };

  return impl as unknown as FirestoreDataConverter<T>;
}

// Helper function to ensure user is authenticated, with automatic token refresh
async function ensureAuthenticated() {
  let user = auth.currentUser;

  if (!user) {
    console.log('No user found, attempting to refresh Firebase token...');
    const refreshSuccess = await refreshFirebaseToken();

    if (refreshSuccess) {
      user = auth.currentUser;
    }

    if (!user) {
      throw new Error('No user signed in and token refresh failed');
    }
  }

  return user;
}

export function createCrudAtoms<T extends object>(collPath: string) {
  const converter = makeConverter<T>();
  const collRef = collection(db, collPath).withConverter(converter);

  const addAtom = atom(null, async (_get, _set, newDoc: Partial<T> & { id?: string }) => {
    try {
      // Ensure user is authenticated (with automatic token refresh)
      const user = await ensureAuthenticated();

      // Use caller-provided id when available, otherwise generate a new one
      const providedId = (newDoc as { id?: string }).id;
      const ref = providedId ? doc(collRef, providedId) : doc(collRef);

      // write everything in one shot
      await setDoc(ref, {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: {
          userId: user.uid,
          displayName: user.displayName || '',
          avatarUrl: user.photoURL || '',
        },
        deleted: false,
        ...(newDoc as WithFieldValue<T>),
        id: ref.id,
      } as WithFieldValue<T>);

      return ref.id;
    } catch (e) {
      const err = e as FirestoreError;
      console.error(`add to ${collPath} failed:`, err);
      throw err;
    }
  });

  const updateAtom = atom(
    null,
    async (_get, _set, { id, data }: { id: string; data: Partial<T> }) => {
      try {
        // Ensure user is authenticated (with automatic token refresh)
        const user = await ensureAuthenticated();

        const ref = doc(collRef, id);
        await updateDoc(ref, {
          ...data,
          updatedAt: serverTimestamp(),
          updatedBy: {
            userId: user.uid,
            displayName: user.displayName || '',
            avatarUrl: user.photoURL || '',
          },
        } as WithFieldValue<T>);
      } catch (e) {
        console.error(`Error updating ${collPath}/${id}:`, e);
        throw e;
      }
    }
  );

  const deleteAtom = atom(null, async (_get, _set, id: string) => {
    try {
      const ref = doc(collRef, id);
      await deleteDoc(ref);
    } catch (e) {
      const err = e as FirestoreError;
      console.error(`delete ${collPath}/${id} failed:`, err);
      throw err;
    }
  });

  return { addAtom, updateAtom, deleteAtom };
}
